/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type {
  FormContext as FormContextType,
  FormStates,
  FormValues,
} from '@shared/types';
import { produce } from "immer";
import createLocalZustand from "@/shared/state/LocalZustand";
import { z } from 'zod';
import { createValidationSlice } from "./store-validation";

export type FormStoreInitializer = {
  formValues?: FormValues;
  formStates?: FormStates;
  required?: string[];
  schema?: z.ZodSchema;
  _onChangeValues?: (value: any) => void;
  _onSubmit?: (value: any) => void;
};

const createFormStore = createLocalZustand<FormContextType>(
  (initializer: FormStoreInitializer) => (set, get) => ({
    formValues: { ...initializer.formValues },
    formStates: { ...initializer.formStates },
    formErrors: {},
    formValidations: {},
    required: initializer.required ?? [],
    validationAttemptCount: 0,
    _schema: initializer.schema ?? undefined,

    set: set,
    get: get,

    updateValues: (newValues: Record<string, string>) =>
      set((state) => {
        const { formValues } = get();
        const newState = produce(state, (draft) => {
          draft.formValues = { ...formValues, ...newValues };
        });
        initializer?._onChangeValues?.(newState);
        return newState;
      }),

    setFieldValues: (values, override = false) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          if (!override) Object.assign(draft.formValues, values);
          else draft.formValues = values;
        });
        initializer?._onChangeValues?.(newState);
        return newState;
      }),

    setFieldValue: (fieldName, value) =>
      set((state) => {
        const newState = produce(state, (draft) => {
          draft.formValues[fieldName] = value;
        });
        initializer?._onChangeValues?.(newState);
        return newState;
      }),

    setFieldState: (fieldName, value) =>
      set((state) =>
        produce(state, (draft) => {
          draft.formStates[fieldName] = value;
        })
      ),

    setFieldError: (fieldName, error) =>
      set((state) =>
        produce(state, (draft) => {
          draft.formErrors[fieldName] = error;
        })
      ),

    ...createValidationSlice(get),

    validate: () => {
      const { formValues, _schema, formErrors, validateSchema, validateRequiredFieldsInStore } = get();

      const result = _schema
        ? validateSchema!(formValues, formErrors)
        : validateRequiredFieldsInStore!(formValues, formErrors);

      let { isValid, errors: newErrors } = result;

      if (isValid && Object.values(newErrors).some(err => err !== undefined)) {
        isValid = false;
      }

      set((state) => ({
        ...state,
        formErrors: newErrors,
        validationAttemptCount: (state.validationAttemptCount ?? 0) + 1
      }));

      return isValid;
    },

    clearForm: () =>
      set((state) => ({
        formValues: {},
        formStates: {},
        formErrors: {},
      })),
  })
);

export default createFormStore;
